import type { AxiosResponse } from 'axios';

import client from 'api/client';
import type {
    BillingReportParams,
    BillingRevenueParams,
    BillingRevenueResponse,
    ServiceProviderAssessmentBillingConfig,
    ServiceProviderBillingConfig,
    ServiceProviderBillingParams,
} from 'types';

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const billingUrl = ({ service_provider_id, country_code }: ServiceProviderBillingParams) =>
    `/service-providers/${service_provider_id}/billing/${country_code}`;
const assessmentBillingUrl = (serviceProviderId: number) =>
    `/service-providers/${serviceProviderId}/billing/assessment`;

export const getBillingConfig = (
    params: ServiceProviderBillingParams
): Promise<AxiosResponse<ServiceProviderBillingConfig>> =>
    client.get<ServiceProviderBillingConfig>(billingUrl(params));

/** Replaces the provider's rules for that country with `default_rule` and `overrides`. */
export const saveBillingConfig = ({
    default_rule,
    overrides,
    ...params
}: ServiceProviderBillingConfig): Promise<AxiosResponse<ServiceProviderBillingConfig>> =>
    client.put<ServiceProviderBillingConfig>(billingUrl(params), { default_rule, overrides });

export const getAssessmentBillingConfig = (
    serviceProviderId: number
): Promise<AxiosResponse<ServiceProviderAssessmentBillingConfig>> =>
    client.get<ServiceProviderAssessmentBillingConfig>(assessmentBillingUrl(serviceProviderId));

export const saveAssessmentBillingConfig = ({
    assessment,
    service_provider_id,
}: ServiceProviderAssessmentBillingConfig): Promise<
    AxiosResponse<ServiceProviderAssessmentBillingConfig>
> =>
    client.put<ServiceProviderAssessmentBillingConfig>(assessmentBillingUrl(service_provider_id), {
        assessment,
    });

export const getRevenue = (
    params: BillingRevenueParams
): Promise<AxiosResponse<BillingRevenueResponse>> =>
    client.get<BillingRevenueResponse>('/analytics/v2/service-provider-billing', {
        params: {
            from: params.from,
            to: params.to,
            ...(params.service_provider_ids?.length
                ? { serviceProviderIds: params.service_provider_ids }
                : {}),
            ...(params.country_codes?.length ? { countryCodes: params.country_codes } : {}),
        },
    });

/** Downloads one provider's current-rule billing workbook. */
export const downloadBillingReport = ({
    service_provider_id,
    from,
    to,
    country_codes,
}: BillingReportParams): Promise<AxiosResponse<Blob>> =>
    client.get<Blob>('/analytics/v2/service-provider-billing/report.xlsx', {
        params: {
            serviceProviderIds: service_provider_id,
            from,
            to,
            ...(country_codes?.length ? { countryCodes: country_codes.join(',') } : {}),
        },
        responseType: 'blob',
        headers: { Accept: XLSX_MIME_TYPE },
    });
